'use client'

import { useState, useEffect, Suspense } from 'react'
import { createSupabaseBrowserClient } from '@/lib/supabase/client'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'

function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [magicLinkLoading, setMagicLinkLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const router = useRouter()
  const searchParams = useSearchParams()

  // Static texts instead of translations
  const texts = {
    signInTitle: 'Sign in to TicketClaim',
    signInSubtitle: 'Access your raffle tickets and manage your entries',
    emailAddress: 'Email Address',
    password: 'Password', 
    signIn: 'Sign In',
    signingIn: 'Signing in...',
    orContinueWithOtherMethods: 'Or try other sign-in methods',
    magicLink: 'Magic Link',
    sendingMagicLink: 'Sending...',
    magicLinkSent: 'Check your email for a magic link to sign in.',
    magicLinkInstruction: 'Please enter your email address first',
    continueWithGoogle: 'Continue with Google',
    redirecting: 'Redirecting...',
    dontHaveAccount: 'Don\'t have an account? Sign up',
    authenticationFailed: 'Authentication failed. Please try again.',
    unexpectedError: 'An unexpected error occurred'
  }

  useEffect(() => {
    const supabase = createSupabaseBrowserClient()
    
    const errorParam = searchParams.get('error')
    if (errorParam === 'callback_error') {
      setError(texts.authenticationFailed)
    }

    // Handle auth state changes for magic link login
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        try {
          await fetch('/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              access_token: session.access_token,
              refresh_token: session.refresh_token,
            }),
          })
          router.push('/')
          router.refresh()
        } catch (fetchError) {
          console.error('Failed to set session cookies:', fetchError)
          // Still try to redirect, in case client-side auth is sufficient
          router.push('/')
          router.refresh()
        }
      }
    })

    return () => {
      authListener.subscription.unsubscribe()
    }
  }, [searchParams, router, texts.authenticationFailed])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const supabase = createSupabaseBrowserClient()

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        setError(error.message)
      } else if (data.session) {
        try {
          await fetch('/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token,
            }),
          })
          router.push('/')
          router.refresh()
        } catch (fetchError) {
          console.error('Failed to set session cookies:', fetchError)
          // Still try to redirect, in case client-side auth is sufficient
          router.push('/')
          router.refresh()
        }
      }
    } catch {
      setError(texts.unexpectedError)
    } finally {
      setLoading(false)
    }
  }

  const handleMagicLink = async () => {
    if (!email) {
      setError(texts.magicLinkInstruction)
      return
    }

    setMagicLinkLoading(true)
    setError(null)
    setMessage(null)

    const supabase = createSupabaseBrowserClient()

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) {
        setError(error.message)
      } else {
        setMessage(texts.magicLinkSent)
      }
    } catch {
      setError(texts.unexpectedError)
    } finally {
      setMagicLinkLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setGoogleLoading(true)
    setError(null)

    const supabase = createSupabaseBrowserClient()

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })

      if (error) {
        setError(error.message)
        setGoogleLoading(false)
      }
      // Don't set loading to false here as the user will be redirected
    } catch {
      setError(texts.unexpectedError)
      setGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {texts.signInTitle}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {texts.signInSubtitle}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          {message && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              {message}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {texts.emailAddress}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={texts.emailAddress}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {texts.password}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={texts.password}
              />
            </div>
          </div>

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? texts.signingIn : texts.signIn}
            </button>

            <div className="text-center">
              <span className="text-sm text-gray-500">{texts.orContinueWithOtherMethods}</span>
            </div>

            <button
              type="button"
              onClick={handleMagicLink}
              disabled={magicLinkLoading || !email}
              className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {magicLinkLoading ? texts.sendingMagicLink : texts.magicLink}
            </button>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {googleLoading ? texts.redirecting : texts.continueWithGoogle}
            </button>
          </div>

          <div className="text-center">
            <Link href="/auth/signup" className="text-sm text-blue-600 hover:text-blue-500">
              {texts.dontHaveAccount}
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Login() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
      <LoginForm />
    </Suspense>
  )
}